import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { getPostBySlug } from "../lib/blogPosts";
import Seo from "../components/Seo";
import { SITE_NAME, absoluteUrl } from "../lib/siteConfig";
import { BOOK_DEMO_URL } from "../lib/constants";
import logo from "../assets/high-resolution-color-logo.png";

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  if (!post) return <Navigate to="/blog" replace />;

  const Content = post.component;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    image: [absoluteUrl(post.image)],
    datePublished: post.date,
    dateModified: post.date,
    author: post.author
      ? { "@type": "Person", name: post.author }
      : { "@type": "Organization", name: `${SITE_NAME} Digital Risk Intelligence` },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: { "@type": "ImageObject", url: absoluteUrl(logo) },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": absoluteUrl(`/blog/${post.slug}`) },
  };

  return (
    <main className="flex-grow pt-16 md:pt-[120px] pb-section-gap px-margin-mobile md:px-gutter">
      <Seo
        title={post.title}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        image={post.image}
        type="article"
        jsonLd={jsonLd}
      />

      <div className="max-w-3xl mx-auto">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 font-label-sm text-label-sm text-on-surface-variant dark:text-surface-variant hover:text-primary transition-colors mb-8"
        >
          <span className="material-symbols-outlined text-[18px]">arrow_back</span>
          Back to Blog
        </Link>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className="font-eyebrow text-eyebrow text-secondary uppercase tracking-widest mb-4 block">
            {post.category}
          </span>
          <h1 className="font-display-hero-mobile text-[32px] md:text-headline-section text-on-surface dark:text-surface-bright mb-5 leading-tight">
            {post.title}
          </h1>
          <div className="flex items-center gap-3 font-data-mono text-data-mono text-on-surface-variant dark:text-surface-variant mb-8">
            <span>{post.displayDate}</span>
            <span>·</span>
            <span className="flex items-center gap-1.5">
              <span className="material-symbols-outlined text-[16px]">schedule</span>
              {post.readTime}
            </span>
            <span>·</span>
            <span>{post.author ? `By ${post.author}` : "CyberMon Digital Risk Intelligence"}</span>
          </div>
        </motion.div>

        <div className="max-w-none">
          <Content />
        </div>

        <div className="mt-16 pt-10 border-t border-glass text-center">
          <h2 className="font-headline-section text-[22px] md:text-headline-section text-on-surface dark:text-surface-bright mb-4">
            Know when your organization is named before it becomes a headline.
          </h2>
          <a
            href={BOOK_DEMO_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center bg-primary-container text-on-primary-container font-headline-card px-6 py-3 rounded-DEFAULT hover:scale-105 transition-transform shadow-sm font-semibold"
          >
            Book a Demo
          </a>
        </div>
      </div>
    </main>
  );
}
